import { Text } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { type ReactElement } from "react";

import { LOG_LEVELS } from "../constants";
import { type LogEntry as LogEntryType } from "../types";
import { formatLogValue } from "../utils";

interface LogEntryProps {
  log: LogEntryType;
}

export function LogEntry({ log }: LogEntryProps): ReactElement {
  const levelConfig = LOG_LEVELS[log.level];

  return (
    <Box
      sx={{
        marginBottom: 1,
        padding: "4px 8px",
        borderLeft: `2px solid ${levelConfig.color}`,
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.02)",
        },
      }}
    >
      {/* Log header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          marginBottom: 2,
        }}
      >
        <Text
          sx={{
            color: levelConfig.color,
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "uppercase",
            minWidth: "fit-content",
          }}
        >
          [{levelConfig.label}]
        </Text>
        <Text
          sx={{
            color: "rgba(255, 255, 255, 0.4)",
            fontSize: "0.7rem",
            fontFamily: "monospace",
          }}
        >
          {log.timestamp.toLocaleTimeString()}
        </Text>
      </Box>

      {/* Log content */}
      <Box>
        {log.args.map((argument, argumentIndex) => (
          <pre
            // eslint-disable-next-line react/no-array-index-key
            key={`arg-${argumentIndex}`}
            style={{
              color: "#e8eaed",
              fontSize: "0.8rem",
              fontFamily:
                "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
              marginBottom: argumentIndex < log.args.length - 1 ? 4 : 0,
              lineHeight: 1.2,
            }}
          >
            {formatLogValue(argument)}
          </pre>
        ))}
      </Box>

      {/* Stack trace for errors */}
      {log.stack && (
        <Box sx={{ marginTop: 4 }}>
          <Text
            sx={{
              fontSize: "0.7rem",
              color: "#ff8a80",
              fontWeight: 500,
              marginBottom: 1,
            }}
          >
            Stack trace:
          </Text>
          <pre
            style={{
              color: "#ffcdd2",
              fontSize: "0.7rem",
              fontFamily:
                "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
              lineHeight: 1.2,
              opacity: 0.8,
            }}
          >
            {log.stack}
          </pre>
        </Box>
      )}
    </Box>
  );
}
